import { getPartnerAccessToken } from '@leagueplatform/sso-util';
import { eligibleUbkList, documentsList } from '../utils/utils';
import * as CONSTANTS from '../utils/constant';

interface InputItem {
  [key: string]: any; // Allows for any properties
  letterTypeDescription?: string;
  fileName?: string;
  requestNumber?: string;
  effectiveDate?: string;
  createDate?: string;
  documentKey?: string;
  url?: string;
  memberName?: string;
  claimNumber?: string;
}

interface OutputItem {
  [key: string]: any; // Allows for any properties
  letterTypeDescription: string;
  createDate: string;
  documentKey: string;
}

export const getEndDate = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
  const currentDay = String(today.getDate()).padStart(2, '0');
  return `${currentYear}-${currentMonth}-${currentDay}`;
};

export const getRecentDocumentListFilerByNinetyDays = (documents: any[]) => {
  const today = new Date();
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(today.getDate() - 90);
  ninetyDaysAgo.setHours(0, 0, 0, 0);

  return documents.filter((item: any) => {
    const itemDate = new Date(item.createDate);
    itemDate.setHours(0, 0, 0, 0);
    return itemDate >= ninetyDaysAgo;
  });
};

export const getDatebyNinetyDaysPrior = () => {
  const today = new Date();
  const ninetyDaysEarlier = new Date(today.setDate(today.getDate() - 90));
  const recentYear = ninetyDaysEarlier.getFullYear();
  const recentMonth = String(ninetyDaysEarlier.getMonth() + 1).padStart(2, '0');
  const recentDate = String(ninetyDaysEarlier.getDate()).padStart(2, '0');
  return `${recentYear}-${recentMonth}-${recentDate}`;
};

export const getDatebyTwoYearsPrior = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const isLeapDay = today.getMonth() === 1 && today.getDate() === 29;
  const twoYearsEarlier = new Date(today.setFullYear(currentYear - 2));
  if (isLeapDay && twoYearsEarlier.getMonth() !== 1) {
    twoYearsEarlier.setMonth(1, 28);
  }
  const allYear = twoYearsEarlier.getFullYear();
  const allMonth = String(twoYearsEarlier.getMonth() + 1).padStart(2, '0');
  const allDate = String(twoYearsEarlier.getDate()).padStart(2, '0');
  return `${allYear}-${allMonth}-${allDate}`;
};

export const formatDate = (inputDate: string) => {
  const date = new Date(inputDate);
  const format: {} = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', format).format(date);
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

export const sortByDate = (arr: any) => {
  const sortedData = arr.sort(
    (
      a: { createDate: string | number | Date },
      b: { createDate: string | number | Date },
    ) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime(),
  );
  return sortedData;
};

export const sortAndRemoveDuplicate = (listData: any[]) => {
  listData.sort(
    (
      a: { createDate: string | number | Date },
      b: { createDate: string | number | Date },
    ) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime(),
  );

  return listData.reduce(
    (
      acc: any[],
      curr: {
        claimNumber: string | null;
        requestNumber: any;
        letterTypeDescription: any;
        createDate: string | number | Date;
      },
    ) => {
      const key =
        curr.claimNumber ||
        (curr.requestNumber && curr.letterTypeDescription
          ? `${curr.requestNumber}-${curr.letterTypeDescription}`
          : null);
      if (key) {
        const existingIndex = acc.findIndex(
          (item: {
            claimNumber: any;
            requestNumber: any;
            letterTypeDescription: any;
          }) =>
            (item.claimNumber && item.claimNumber === curr.claimNumber) ||
            (item.requestNumber &&
              item.requestNumber === curr.requestNumber &&
              item.letterTypeDescription === curr.letterTypeDescription),
        );
        if (existingIndex === -1) {
          acc.push(curr);
        } else if (
          new Date(curr.createDate) > new Date(acc[existingIndex].createDate)
        ) {
          acc[existingIndex] = curr;
        }
      }
      return acc;
    },
    [],
  );
};

export async function getAccessTokenHelper(
  url: string,
  token: string | null,
  ubk: string | null,
) {
  const requestOptions = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...(ubk && { brandUbk: ubk }),
    },
  };

  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    const message = `HTTP error! Status: ${response.status}, Endpoint: ${url}`;
    throw new Error(message);
  }
  const result = response.json();
  return result;
}

export async function getAccessTokensForMultipleDocs(
  endpoints: string[],
  accessToken: string | null,
  ubk: string | null,
): Promise<any> {
  const requests = endpoints.map((endpoint) =>
    getAccessTokenHelper(endpoint, accessToken, ubk)
      .then((result) => ({ success: true, data: result, endpoint }))
      .catch((error) => ({ success: false, error, endpoint })),
  );
  try {
    return await Promise.all(requests);
  } catch (error) {
    return [];
  }
}

export const getDocumentsEndPoints = (
  type: string,
  baseUrl: string,
  axwayBaseUrl: string | undefined,
  fireBaseFlagsConfig: any,
  ubk?: string | null,
  medicareMember?: boolean,
  groupNumber?: string | undefined,
) => {
  const eligibleUser = !ubk || (ubk && eligibleUbkList.includes(ubk));
  const enableUMLetters = fireBaseFlagsConfig?.um_letters || false;
  const enableWelcomeGuides = fireBaseFlagsConfig?.welcome_guides || false;
  const eobParams = `${baseUrl}/digital/v1/members/claims/eob/list`;
  const umLetterParams = `${axwayBaseUrl}/digital/v1/members/documents/ext/um-letters/list`;
  const welcomeGuides = `${axwayBaseUrl}/digital/v1/members/documents/ext/ma-welcome-guides`;
  const allEobUrl = [
    `${eobParams}?endDate=${getEndDate()}&startDate=${getDatebyTwoYearsPrior()}`,
  ];
  const allUMlettersUrl = [
    `${umLetterParams}?endDate=${getEndDate()}&startDate=${getDatebyTwoYearsPrior()}&letterType=approval`,
    `${umLetterParams}?endDate=${getEndDate()}&startDate=${getDatebyTwoYearsPrior()}&letterType=denial`,
  ];
  const recentLetters =
    eligibleUser && enableUMLetters
      ? [
          `${eobParams}?endDate=${getEndDate()}&startDate=${getDatebyNinetyDaysPrior()}`,
          `${umLetterParams}?endDate=${getEndDate()}&startDate=${getDatebyNinetyDaysPrior()}&letterType=approval`,
          `${umLetterParams}?endDate=${getEndDate()}&startDate=${getDatebyNinetyDaysPrior()}&letterType=denial`,
          `${welcomeGuides}?groupNumber=${groupNumber}`,
        ]
      : [
          `${eobParams}?endDate=${getEndDate()}&startDate=${getDatebyNinetyDaysPrior()}`,
          `${welcomeGuides}?groupNumber=${groupNumber}`,
        ];
  const allWelcomeGuidesUrl = [`${welcomeGuides}?groupNumber=${groupNumber}`];

  if (!medicareMember || !enableWelcomeGuides) {
    recentLetters.pop();
  }

  switch (type) {
    case CONSTANTS.RECENT:
      return recentLetters;
    case CONSTANTS.EOB:
      return allEobUrl;
    case CONSTANTS.DETERMINATION_NOTICES:
      return allUMlettersUrl;
    case CONSTANTS.WELCOME_GUIDES:
      return allWelcomeGuidesUrl;
    default:
      return [];
  }
};

export const getPartnerToken = async () => {
  try {
    const partnerAccessToken = await getPartnerAccessToken('highmark');
    return { success: true, token: partnerAccessToken };
  } catch (error) {
    return { success: false, errMsg: error };
  }
};

export async function getPDF(url: string, token: string, ubk: string) {
  const requestOptions = {
    method: 'GET',

    headers: new Headers({
      Accept: 'application/pdf',
      Authorization: `Bearer ${token}`,
      ...(ubk && { brandUbk: ubk }),
    }),
  };
  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(
        `This is an HTTP error: The status is ${response.status}`,
      );
    }
    const blob = await response.blob();
    const pdfUrl = URL.createObjectURL(blob);
    window.open(pdfUrl, '_blank');
    return { success: true };
  } catch (error) {
    const errObj = {
      errMsg: error,
    };
    return errObj;
  }
}

function mapResponse(data: InputItem[]): OutputItem[] {
  return data.map((item) => {
    const mappedItem: OutputItem = {
      letterTypeDescription: item.letterTypeDescription || item.guideName || '',
      createDate: item.effectiveDate || item.createDate || '',
      documentKey: item.documentKey || item.url || '',
      documentType: item.url && item.effectiveDate ? 'welcome' : 'other',
      ...Object.fromEntries(
        Object.entries(item).filter(
          ([key]) =>
            ![
              'letterTypeDescription',
              'doccumentType',
              'fileName',
              'effectiveDate',
              'createDate',
              'documentKey',
              'url',
            ].includes(key),
        ),
      ),
    };
    return mappedItem;
  });
}

export const handleDocumentsConcat = (documents: any) => {
  const consolidatedList: any = [];
  let hasErrors = false;
  if (!documents) {
    return { error: false, list: consolidatedList };
  }
  documents.some(
    (item: {
      success: boolean;
      data: { umLetterList: any; eobList: any; welcomeGuides: any };
    }) => {
      if (!item.success) {
        hasErrors = true;
        return true;
      }
      const letterList =
        item.data?.umLetterList ||
        item.data?.eobList ||
        item.data?.welcomeGuides ||
        [];
      consolidatedList.push(...letterList);
      return false;
    },
  );
  const result = { error: hasErrors, list: mapResponse(consolidatedList) };
  return result;
};

export const getDocumentsList = (
  ubkValue: string | undefined | null,
  medicareMember: boolean | undefined,
  fireBaseFlagsConfig: any,
) => {
  const documentTypes = { ...documentsList };
  const eligibleUser =
    !ubkValue || (ubkValue && eligibleUbkList.includes(ubkValue));
  const enableUMLetters = fireBaseFlagsConfig?.um_letters;
  const enableWelcomeGuides = fireBaseFlagsConfig?.welcome_guides;
  if (
    (!eligibleUser || !enableUMLetters) &&
    documentTypes['determination-notices']
  ) {
    delete documentTypes['determination-notices'];
  }
  if (
    (!medicareMember || !enableWelcomeGuides) &&
    documentTypes['ma-welcome-guides']
  ) {
    delete documentTypes['ma-welcome-guides'];
  }
  return documentTypes;
};
